/* eslint-disable react/prop-types */
import React from 'react';
import i18n from 'i18next';
import Helmet from 'react-helmet';
import {
  isEditorActive,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import { layoutServiceFactory } from './lib/layout-service-factory';
import config from './temp/config';
import Layout from './Layout';
import NotFound from './NotFound';
import resolveSite from './SiteResolver';
import { getDefaultLanguage } from './utils/languageHelper';

/* eslint-disable no-console */

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

class RouteHandler extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: true,
      defaultLanguage: config.defaultLanguage,
    };

    const { sitecoreContext: routeData } = this.props;

    // route data from react-router - if route was resolved, it's not a 404
    if (props.route !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (routeData && !routeData.route) {
      this.state.notFound = true;
    }

    // if we have an SSR state, and that state has language data, set the current language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /de-DE) will override this default; this is for home.
    if (routeData && routeData.language) {
      this.state.defaultLanguage = routeData.language;
    }

    // tell i18next to sync its current language with the route language
    this.updateLanguage();
  }

  componentDidMount() {
    // if no existing routeData is present (from SSR), get Layout Service fetching the route data or ssr render complete
    const { sitecoreContext, ssrRenderComplete } = this.props;
    if (!sitecoreContext || ssrRenderComplete) {
      this.updateRouteData();
    }
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.route.match.url;
    // const newRoute = this.props.route.match.url;
    const {
      route: {
        match: { url: newRoute },
      },
    } = this.props;

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }

    // if in Sitecore editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLanguage();
    this.updateRouteData();
  }

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateRouteData() {
    const locale = getDefaultLanguage();

    const {
      route: {
        match: {
          params: { sitecoreRoute, lang },
        },
      },
    } = this.props;
    const { defaultLanguage } = this.state;
    let sitecoreRoutePath = sitecoreRoute || '/';

    if (!sitecoreRoutePath.startsWith('/')) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    const language = lang || defaultLanguage;

    // instantiate the dictionary service.
    const layoutServiceInstance = layoutServiceFactory.create();

    // get the route data for the new route
    const { updateSitecoreContext } = this.props;
    layoutServiceInstance
      .fetchLayoutData(sitecoreRoutePath, locale || language)
      .then((routeData) => {
        if (
          routeData !== null &&
          routeData.sitecore &&
          routeData.sitecore.route
        ) {
          // set the sitecore context data and push the new route
          updateSitecoreContext({
            route: routeData.sitecore.route,
            itemId: routeData.sitecore.route.itemId,
            ...routeData.sitecore.context,
          });
          const { notFound } = this.state;
          if (notFound) {
            this.setState({ notFound: false });
          }
        } else {
          this.setState({ notFound: true }, () => {
            const context =
              routeData && routeData.sitecore
                ? routeData.sitecore.context
                : null;

            updateSitecoreContext(context);
          });
        }
      });
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage() {
    const { locale } = resolveSite();

    const {
      route: {
        match: {
          params: { lang },
        },
      },
    } = this.props;
    const { defaultLanguage } = this.state;
    // eslint-disable-next-line no-unused-vars
    const newLanguage = lang || defaultLanguage;
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }

  render() {
    const { lang, locale } = resolveSite();
    const [, localCountry] = locale.split('-');
    const urlParams = new Map(
      window.location.search
        .slice(1)
        .split('&')
        .map((kv) => kv.split('=')),
    );

    const {
      sitecoreContext,
      route: {
        match: {
          params: { country = localCountry?.toLowerCase() },
        },
      },
    } = this.props;
    const { notFound } = this.state;
    const routeData = sitecoreContext;

    // no route data for the current route in Sitecore - show not found component.
    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    if (notFound && routeData) {
      const homePage = `/${country}/${lang}/home`;
      return (
        <div>
          <Helmet>
            <title>{i18n.t('Page not found')}</title>
          </Helmet>
          <NotFound homePage={homePage} context={routeData} />
        </div>
      );
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!routeData) {
      return null;
    }
    // Render the app's root structural layout
    return (
      <Layout
        route={routeData.route}
        lang={lang}
        locale={locale}
        country={country}
        urlParams={urlParams}
      />
    );
  }
}

export default withSitecoreContext({ updatable: true })(RouteHandler);
